import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ArrowLeft, Calendar, Search, Building2, MapPin, Users } from 'lucide-react';
import { toast } from 'sonner';

import { formatDate } from '../../utils/timeFormat';
// Iter-211 — Admin Bookings list view.
// Restores the `/admin/bookings` route (was 404'ing because the
// route existed only as a navigation target, not a mounted page).
// Consumes the existing `/admin/calendar/bookings` endpoint
// (already filters test fixtures via is_test).

const TABS = [
  { value: 'upcoming',         label: 'Upcoming',         status: 'open' },
  { value: 'partially_filled', label: 'Partially Filled', status: 'partially_filled' },
  { value: 'filled',           label: 'Confirmed',        status: 'filled' },
  { value: 'past',             label: 'Past',             status: null },
  { value: 'cancelled',        label: 'Cancelled',        status: 'cancelled' },
];

const AdminBookings = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('upcoming');
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const def = TABS.find((t) => t.value === tab);
      const params = {};
      if (def?.status) params.status = def.status;
      if (tab === 'past') {
        params.end_date = new Date(Date.now() - 6 * 3600 * 1000).toISOString().slice(0, 10);
      } else {
        params.start_date = new Date().toISOString().slice(0, 10);
      }
      const r = await api.get('/admin/calendar/bookings', { params });
      setRows(r.data.bookings || r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load bookings');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchRows(); /* eslint-disable-next-line */ }, [tab]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((b) =>
      (b.venue_name || '').toLowerCase().includes(q)
      || (b.client_name || '').toLowerCase().includes(q)
      || (b.role_name || '').toLowerCase().includes(q)
      || (b.city || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-bookings-page">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/home')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl font-bold">Bookings</h1>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList className="flex flex-wrap">
          {TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`tab-${t.value}`}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mb-4 flex items-center gap-2">
        <Search className="w-4 h-4 text-zinc-400" />
        <Input
          placeholder="Search venue, client, role, city..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="bookings-search-input"
        />
      </div>

      {loading ? (
        <p className="text-zinc-500">Loading bookings...</p>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-zinc-500" data-testid="empty-state">No bookings.</CardContent></Card>
      ) : (
        <div className="grid gap-3" data-testid="bookings-list">
          {filtered.map((b) => (
            <Card
              key={b.id}
              className="hover:border-cyan-500/40 cursor-pointer transition-colors"
              onClick={() => navigate(`/admin/shifts/${b.id}`)}
              data-testid={`booking-row-${b.id}`}
            >
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Building2 className="w-4 h-4 text-zinc-400 shrink-0" />
                    <span className="font-medium truncate">{b.venue_name || '—'}</span>
                    {b.is_featured && <Badge variant="outline" className="text-xs">Premium</Badge>}
                    {b.tier_required === 'premium' && <Badge variant="outline" className="text-xs">Premium</Badge>}
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        b.staffing_status === 'filled' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : b.staffing_status === 'cancelled' ? 'bg-rose-50 text-rose-700 border-rose-200'
                        : b.staffing_status === 'partially_filled' ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-zinc-50 text-zinc-700 border-zinc-200'
                      }`}
                    >
                      {b.staffing_status || b.status || 'open'}
                    </Badge>
                  </div>
                  <div className="text-sm text-zinc-600 truncate">
                    {b.role_name} · {b.client_name || '—'}
                  </div>
                  <div className="text-xs text-zinc-400 mt-1 flex flex-wrap gap-x-3">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {b.shift_date ? formatDate(b.shift_date) : '—'}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {[b.city, b.state].filter(Boolean).join(', ')}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {b.confirmed_count ?? 0}/{b.talent_needed ?? 0} staffed
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBookings;
